import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';

import { goToInitOrder } from 'core/api/actions/navigation';
import Money from 'core/api/Money';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import { Image } from 'core/ui/atoms';
import { UPDATE_USER_OTP_SUBSCRIPTION_STATUS, PAUSE_OTP_SUBSCRIPTION, ACTIVATE_OTP_SUBSCRIPTION, CREATE_DELIVERY_SKIPS } from 'core/api/apollo/mutations';
import { LIST_SUBSCRIPTION_STATUS_REASONS, FETCH_USER_DELIVERY_SKIPS } from 'core/api/apollo/queries';

const C = {
  greenDark: '#2D3A2E', greenMed: '#4A6741', greenLight: '#E8EDE8', greenAccent: '#A8C5A0',
  bg: '#FAFAF8', white: '#FFFFFF', text: '#1A1A1A', muted: '#6B7280', border: '#E0E0E0',
  cardBorder: '#D4D4D4', red: '#DC2626', redLight: '#FEF2F2', redBorder: '#FECACA',
  amber: '#92400E', amberLight: '#FFFBEB', amberBorder: '#FCD34D',
};
const font = 'system-ui, -apple-system, sans-serif';

const SubscriptionItem = ({ item, refetch }) => {
  const { programType, subscriptionActive, mealType, bundlePrice, subscriptionStatus } = item;
  const { name, availableDeliveryDays = [], schedule } = item?.programBundle || {};
  const isSubscription = subscriptionActive !== null;

  const [fetchReasons, { data: reasonsData, loading: reasonsLoading }] = useLazyQuery(LIST_SUBSCRIPTION_STATUS_REASONS);
  const cancelReasons = reasonsData?.listSubscriptionStatusReasons || [];

  const { data: skipsData, refetch: refetchSkips } = useQuery(FETCH_USER_DELIVERY_SKIPS);
  const deliverySkips = skipsData?.fetchUserDeliverySkips || [];

  const [screen, setScreen] = useState('default');
  const [pauseLoading, setPauseLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [skipError, setSkipError] = useState(null);
  const [skipResult, setSkipResult] = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [surveyDetail, setSurveyDetail] = useState('');

  const selectedReason = cancelReasons.find((r) => r.id === selectedReasonId);

  // Find the first unlocked, unassigned delivery period
  const firstUnlockedWeek = deliverySkips.find((s) => !s.deliveryPeriod?.locked && !s.deliveryPeriod?.assigned);
  // Only allow skipping if that week is not already skipped
  const nextSkippableWeek = firstUnlockedWeek && !firstUnlockedWeek.skipped ? firstUnlockedWeek : null;

  const [createDeliverySkips] = useMutation(CREATE_DELIVERY_SKIPS, {
    onCompleted: (data) => {
      if (data?.createDeliverySkips?.message) {
        setSkipResult(data.createDeliverySkips);
        setScreen('skip-done');
      } else if (data?.createDeliverySkips?.errors?.length) {
        setSkipError(data.createDeliverySkips.errors[0].message);
        setScreen('skip-error');
      }
      refetchSkips();
      setSkipLoading(false);
    },
    onError: (error) => {
      console.error('Error skipping delivery:', error);
      setSkipError('An unexpected error occurred while skipping the week.');
      setScreen('skip-error');
      setSkipLoading(false);
    },
  });

  const [skipTargetPeriodId, setSkipTargetPeriodId] = useState(null);

  const handleSkipNextWeek = async () => {
    if (!nextSkippableWeek || skipLoading) return;
    const periodId = nextSkippableWeek.deliveryPeriod.id;
    // Prevent skipping a different week on repeated clicks
    if (skipTargetPeriodId && skipTargetPeriodId !== periodId) return;
    setSkipTargetPeriodId(periodId);
    setSkipLoading(true);
    setSkipError(null);
    try {
      await createDeliverySkips({
        variables: {
          input: {
            deliveryPeriodIds: [periodId],
          },
        },
      });
    } catch (error) {
      console.error('Failed to skip week:', error);
      setSkipLoading(false);
    }
  };

  const [updateUserOtpSubscription] = useMutation(UPDATE_USER_OTP_SUBSCRIPTION_STATUS, {
    onCompleted: async (data) => {
      const errors = data?.updateUserOtpSubscriptionStatus?.errors || [];
      if (errors.length > 0) {
        console.error('Cancellation returned errors:', errors);
        setCancelLoading(false);
        return;
      }
      refetch();
      setScreen('cancel-done');
      setCancelLoading(false);
    },
    onError: (error) => {
      console.error('Error cancelling user OTP:', error);
      setCancelLoading(false);
    },
  });

  const [pauseOtpSubscription] = useMutation(PAUSE_OTP_SUBSCRIPTION, {
    onCompleted: async () => {
      refetch();
      setScreen('pause-done');
      setPauseLoading(false);
    },
    onError: (error) => {
      console.error('Error pausing OTP subscription:', error);
      setPauseLoading(false);
    },
  });

  const [activateOtpSubscription] = useMutation(ACTIVATE_OTP_SUBSCRIPTION, {
    onCompleted: async () => {
      refetch();
      setScreen('unpause-done');
    },
    onError: (error) => {
      console.error('Error activating OTP subscription:', error);
    },
  });

  const handleActivate = async () => {
    try {
      await activateOtpSubscription({ variables: { input: {} } });
    } catch (error) {
      console.error('Failed to activate subscription:', error);
    }
  };

  const handleCancellation = async () => {
    setCancelLoading(true);
    try {
      await updateUserOtpSubscription({
        variables: {
          input: {
            status: 'cancelled',
            reasonId: selectedReasonId || undefined,
            memo: surveyDetail || undefined,
          },
        },
      });
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setCancelLoading(false);
    }
  };

  const handlePause = async () => {
    setPauseLoading(true);
    try {
      await pauseOtpSubscription({ variables: { input: {} } });
    } catch (error) {
      console.error('Failed to pause subscription:', error);
      setPauseLoading(false);
    }
  };

  if (isEmpty(item)) {
    return null;
  }

  const lockedOrders = availableDeliveryDays.filter((deliveryDay) => deliveryDay.locked);
  const nextDeliveryDay = head(lockedOrders)?.date;
  const firstUnlockedDay = availableDeliveryDays.find((deliveryDay) => !deliveryDay.locked);
  const nextDeliveryFormatted = nextDeliveryDay ? moment(nextDeliveryDay).format('MMMM D') : '';
  const isProgramCompleted = !isSubscription && (
    availableDeliveryDays.length === 0 ||
    availableDeliveryDays.every((day) => moment(day.date).isBefore(moment(), 'day'))
  );

  const resetFlowState = () => {
    setSelectedReasonId('');
    setSurveyDetail('');
  };

  // --- Pause Confirm Screen ---
  if (screen === 'pause-confirm') {
    return (
      <FlowContainer>
        <BackButton onClick={() => setScreen('default')}>{'<-'} Back to Settings</BackButton>
        <FlowTitle>Pause Your Subscription</FlowTitle>
        <FlowDescription>
          Take a break without losing anything. Your program, preferences, and pricing all stay locked in. No charges while paused. Come back whenever you are ready.
        </FlowDescription>
        {lockedOrders.length > 0 && (
          <AmberAlert>
            <strong>Heads up:</strong>{' '}
            {lockedOrders.length === 1
              ? `This delivery is past the pause deadline and is still scheduled: ${moment(lockedOrders[0].date).format('ddd, MMM Do')}. Your pause begins after this delivery.`
              : `These deliveries are past the pause deadline and are still scheduled: ${lockedOrders.map((o) => moment(o.date).format('ddd, MMM Do')).join('; ')}. Your pause begins after these deliveries.`
            }
          </AmberAlert>
        )}
        <ActionRow>
          <PrimaryButton disabled={pauseLoading} onClick={handlePause}>
            {pauseLoading ? 'Pausing...' : 'Pause My Subscription'}
          </PrimaryButton>
          <GhostButton disabled={pauseLoading} onClick={() => setScreen('default')}>
            Never mind
          </GhostButton>
        </ActionRow>
      </FlowContainer>
    );
  }

  // --- Pause Done Screen ---
  if (screen === 'pause-done') {
    return (
      <DoneContainer>
        <IconCircle>||</IconCircle>
        <FlowTitle style={{ textAlign: 'center' }}>Subscription Paused</FlowTitle>
        <DoneDescription>
          You are all set. Your subscription is paused until you decide to come back. Resume anytime from your settings.
        </DoneDescription>
        <DetailsCard>
          <DetailsLabel>Pause Details</DetailsLabel>
          <DetailsContent>
            Paused: {moment().format('MMMM D, YYYY')}<br/>
            Program: {programType}<br/>
            {nextDeliveryDay && <>Last delivery: {moment(nextDeliveryDay).format('ddd, MMM Do')}<br/></>}
            <span style={{ color: C.greenMed }}>Your program, preferences, and pricing are saved.</span>
          </DetailsContent>
        </DetailsCard>
        <OutlineButton onClick={() => setScreen('default')}>Return to Settings</OutlineButton>
      </DoneContainer>
    );
  }

  // --- Unpause Done Screen ---
  if (screen === 'unpause-done') {
    return (
      <DoneContainer>
        <IconCircle style={{ fontSize: 20 }}>&#9654;</IconCircle>
        <FlowTitle style={{ textAlign: 'center' }}>Subscription Unpaused</FlowTitle>
        <DoneDescription>
          Your account is unpaused.{firstUnlockedDay
            ? ` Your deliveries resume with your delivery on ${moment(firstUnlockedDay.date).format('dddd, MMM Do')}.`
            : ' Your deliveries will resume shortly.'}
        </DoneDescription>
        <OutlineButton onClick={() => setScreen('default')}>Return to Settings</OutlineButton>
      </DoneContainer>
    );
  }

  // --- Skip Done Screen ---
  if (screen === 'skip-done') {
    const skippedPeriod = skipResult;
    return (
      <DoneContainer>
        <IconCircle style={{ fontSize: 20 }}>&#10003;</IconCircle>
        <FlowTitle style={{ textAlign: 'center' }}>Week Skipped</FlowTitle>
        <DoneDescription>
          {skippedPeriod?.message || 'Your delivery has been skipped. You will not be charged for this week.'}
          {skippedPeriod?.hasPaidMealSwaps && (
            <span style={{ display: 'block', marginTop: 12, color: C.amber }}>
              Since you had previously swapped items for this week, any amount charged for these items will be refunded back to your card and any amount credited for these items will be removed from your account.
            </span>
          )}
        </DoneDescription>
        <OutlineButton onClick={() => { setScreen('default'); setSkipResult(null); setSkipTargetPeriodId(null); }}>Return to Settings</OutlineButton>
      </DoneContainer>
    );
  }

  // --- Skip Error Screen ---
  if (screen === 'skip-error') {
    return (
      <DoneContainer>
        <IconCircle style={{ background: C.redLight, color: C.red, fontSize: 20 }}>!</IconCircle>
        <FlowTitle style={{ textAlign: 'center' }}>Can&apos;t Skip This Week</FlowTitle>
        <DoneDescription>{skipError}</DoneDescription>
        <OutlineButton onClick={() => { setScreen('default'); setSkipError(null); setSkipTargetPeriodId(null); }}>Return to Settings</OutlineButton>
      </DoneContainer>
    );
  }

  // --- Cancel Step 1: Pause Offer ---
  if (screen === 'cancel-1') {
    return (
      <FlowContainer>
        <BackButton onClick={() => { setScreen('default'); resetFlowState(); }}>{'<-'} Back to Settings</BackButton>
        <FlowTitle>Cancel Your Subscription</FlowTitle>
        <FlowDescription style={{ marginBottom: 28 }}>
          We are sorry to see you go. Before you cancel, would you consider pausing instead?
        </FlowDescription>
        <PauseOfferCard>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.greenDark, fontFamily: font, marginBottom: 8 }}>Take a break instead?</div>
          <p style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.65, marginBottom: 6 }}>
            Pausing saves everything: your program, your preferences, and your pricing. No charges while paused. Resume whenever you are ready with one click.
          </p>
          <p style={{ fontSize: 13, fontFamily: font, color: C.greenMed, lineHeight: 1.5, marginBottom: 16 }}>
            Most customers who pause come back within 3 weeks.
          </p>
          <PrimaryButton onClick={() => setScreen('pause-confirm')}>Pause My Subscription</PrimaryButton>
        </PauseOfferCard>
        <ContinueCancelCard>
          <div style={{ fontSize: 14, fontWeight: 600, fontFamily: font, color: C.text, marginBottom: 10 }}>Continue with cancellation</div>
          {lockedOrders.length > 0 && (
            <AmberAlert style={{ marginBottom: 14, padding: 12, borderRadius: 6 }}>
              {lockedOrders.length === 1
                ? <>Your delivery on <strong>{moment(lockedOrders[0].date).format('ddd, MMM Do')}</strong> has already been finalized and will still be shipped.</>
                : <>Your deliveries on <strong>{lockedOrders.map((o) => moment(o.date).format('ddd, MMM Do')).join('; ')}</strong> have already been finalized and will still be shipped.</>
              }{' '}Cancellation takes effect after {lockedOrders.length === 1 ? 'this delivery' : 'these deliveries'}.
            </AmberAlert>
          )}
          <ul style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.8, paddingLeft: 18, marginBottom: 14 }}>
            {nextDeliveryDay && <li>Your {nextDeliveryFormatted} delivery will arrive as scheduled.</li>}
            <li>You will not be charged for any future weeks.</li>
            <li>You can resubscribe at any time.</li>
          </ul>
          <DangerOutlineButton onClick={() => setScreen('cancel-survey')}>Cancel My Subscription</DangerOutlineButton>
        </ContinueCancelCard>
      </FlowContainer>
    );
  }

  // --- Cancel Survey Screen ---
  if (screen === 'cancel-survey') {
    return (
      <FlowContainer>
        <BackButton onClick={() => setScreen('cancel-1')}>{'<-'} Back</BackButton>
        <FlowTitle>What is the main reason you are canceling?</FlowTitle>
        <FlowDescription>This helps us improve Methodology for everyone. Select one.</FlowDescription>
        {reasonsLoading && cancelReasons.length === 0 ? (
          <FlowDescription style={{ textAlign: 'center', color: C.muted }}>Loading reasons...</FlowDescription>
        ) : (
          <>
            <ReasonGrid>
              {cancelReasons.map((r) => (
                <ReasonLabel
                  key={r.id}
                  onClick={() => setSelectedReasonId(r.id)}
                  $selected={selectedReasonId === r.id}
                >
                  <RadioCircle $selected={selectedReasonId === r.id}>
                    {selectedReasonId === r.id && <RadioDot />}
                  </RadioCircle>
                  {r.reason}
                </ReasonLabel>
              ))}
            </ReasonGrid>
            {selectedReason?.requireMemo && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontFamily: font, color: C.muted, display: 'block', marginBottom: 6 }}>Anything else? (optional)</label>
                <SurveyTextarea
                  value={surveyDetail}
                  onChange={(e) => setSurveyDetail(e.target.value)}
                  placeholder="Your feedback helps us improve..."
                />
              </div>
            )}
            <DangerOutlineButton
              disabled={!selectedReasonId}
              onClick={() => setScreen('cancel-confirm')}
              style={{
                cursor: selectedReasonId ? 'pointer' : 'not-allowed',
                color: selectedReasonId ? C.red : '#9CA3AF',
                borderColor: selectedReasonId ? C.red : '#D1D5DB',
              }}
            >
              Continue with Cancellation
            </DangerOutlineButton>
          </>
        )}
      </FlowContainer>
    );
  }

  // --- Cancel Confirm Screen ---
  if (screen === 'cancel-confirm') {
    return (
      <FlowContainer>
        <BackButton onClick={() => setScreen('cancel-survey')}>{'<-'} Back</BackButton>
        <FlowTitle>Confirm Cancellation</FlowTitle>
        <FlowDescription>Please confirm you would like to cancel your Methodology subscription.</FlowDescription>
        <RedAlert>
          <div style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.7 }}>
            <strong>Program:</strong> {programType}<br/>
            <strong>Meal type:</strong> {mealType}<br/>
            {nextDeliveryDay && <><strong>Last delivery:</strong> Week of {nextDeliveryFormatted}<br/></>}
            <strong>No future charges</strong> will be applied.
          </div>
        </RedAlert>
        <LoseAccessCard>
          <div style={{ fontWeight: 600, color: C.text, marginBottom: 6, fontSize: 13 }}>When you cancel, you will lose access to:</div>
          <div>Your current program settings and meal preferences</div>
          <div>Weekly curated menus tailored to your goals</div>
          <div style={{ marginTop: 8, color: C.greenMed, fontWeight: 500 }}>
            Pausing preserves all of this.{' '}
            <span onClick={() => setScreen('pause-confirm')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Pause instead?</span>
          </div>
        </LoseAccessCard>
        <ActionRow>
          <DangerButton disabled={cancelLoading} onClick={handleCancellation}>
            {cancelLoading ? 'Cancelling...' : 'Yes, Cancel My Subscription'}
          </DangerButton>
          <SecondaryButton onClick={() => { setScreen('default'); resetFlowState(); }}>Keep My Subscription</SecondaryButton>
        </ActionRow>
      </FlowContainer>
    );
  }

  // --- Cancel Done Screen ---
  if (screen === 'cancel-done') {
    return (
      <DoneContainer>
        <IconCircle style={{ fontSize: 22, fontWeight: 700 }}>OK</IconCircle>
        <FlowTitle style={{ textAlign: 'center' }}>Subscription Cancelled</FlowTitle>
        <DoneDescription>
          Your subscription has been cancelled. You will receive a confirmation email shortly.
          {nextDeliveryDay && ` Your delivery for the week of ${nextDeliveryFormatted} will still arrive as scheduled.`}
        </DoneDescription>
        <DetailsCard style={{ maxWidth: 380 }}>
          <DetailsLabel>Confirmation</DetailsLabel>
          <DetailsContent>
            Cancelled: {moment().tz('America/Los_Angeles').format('MMMM D, YYYY [at] h:mm A')} PT<br/>
            Program: {programType}<br/>
            {nextDeliveryDay && <>Last delivery: Week of {nextDeliveryFormatted}<br/></>}
          </DetailsContent>
        </DetailsCard>
        <p style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.6, marginBottom: 8, fontWeight: 500 }}>We would love to cook for you again.</p>
        <p style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>You can resubscribe anytime.</p>
        <OutlineButton onClick={() => { setScreen('default'); resetFlowState(); }}>Return to Settings</OutlineButton>
      </DoneContainer>
    );
  }

  // --- Default: Subscription Card ---
  return (
    <SubscriptionCardWrapper>
      <CardHeader>
        <CardHeaderTitle>{isSubscription ? 'Subscription' : 'Program'}</CardHeaderTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flex: 1 }}>
            <ProductImage src={PRODUCT_TEMP} alt={`${name || programType} meal program subscription`} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, fontFamily: font, color: C.text, marginBottom: 4 }}>{programType}</div>
              <div style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6 }}>
                {mealType}<br/>
                {isSubscription ? `Subscription | ${schedule?.name}/ Week` : `One-time | ${schedule?.name}`}<br/>
                {isSubscription
                  ? <>{Money(bundlePrice).toFormat('$0,0')}/week</>
                  : <>{Money(bundlePrice).toFormat('$0,0')}</>
                }
                {nextDeliveryDay && <>, Next delivery: {moment(nextDeliveryDay).format('dddd, MMMM D')}</>}
              </div>
            </div>
          </div>
          <StatusBadge $status={isSubscription ? subscriptionStatus : isProgramCompleted ? 'completed' : 'purchased'}>
            {!isSubscription
              ? (isProgramCompleted ? 'Completed' : 'Purchased')
              : subscriptionStatus === 'paused' ? 'Paused' : subscriptionStatus === 'cancelled' ? 'Cancelled' : 'Active'
            }
          </StatusBadge>
        </div>

        {subscriptionActive !== null && (
          <CardActions>
            {subscriptionStatus === 'cancelled' ? (
              <PrimaryButton onClick={() => goToInitOrder()}>Activate Subscription</PrimaryButton>
            ) : subscriptionStatus === 'paused' ? (
              <PrimaryButton onClick={handleActivate}>Unpause Subscription</PrimaryButton>
            ) : (
              <>
                {nextSkippableWeek && (
                  <ActionOutlineButton disabled={skipLoading} onClick={handleSkipNextWeek}>
                    {skipLoading ? 'Skipping...' : 'Skip Next Week'}
                  </ActionOutlineButton>
                )}
                <ActionOutlineButton onClick={() => setScreen('pause-confirm')}>Pause Subscription</ActionOutlineButton>
                <DangerOutlineButton onClick={() => { fetchReasons(); setScreen('cancel-1'); }} style={{ marginLeft: 'auto' }}>Cancel Subscription</DangerOutlineButton>
              </>
            )}
          </CardActions>
        )}

        <TermsText>
          {isSubscription
            ? 'Your subscription renews automatically each week until you cancel. Payment is processed each Sunday, and your Monday delivery that is 8 days out is finalized at that time. Any changes must be made by Sunday at 11:00 PM PT and take effect for the Monday delivery that is 8 days after that.'
            : isProgramCompleted
              ? 'All deliveries for your program have been completed. Thank you for choosing Methodology!'
              : 'This is a one-time program and will not renew or charge you again. Your deliveries are scheduled based on the program you selected at checkout. Any changes to your upcoming meals must be made by Sunday at 11:00 PM PT to take effect for the following week\'s delivery.'
          }{' '}
          <a href='https://gomethodology.com/terms-conditions' rel="noreferrer" target='_blank' style={{ color: C.greenMed, textDecoration: 'underline' }}>
            Terms and Conditions
          </a>
        </TermsText>
      </CardBody>
    </SubscriptionCardWrapper>
  );
};

SubscriptionItem.propTypes = {
  item: PropTypes.object.isRequired,
  refetch: PropTypes.func,
};

// --- Styled Components ---

const SubscriptionCardWrapper = styled.div`
  border: 1px solid ${C.cardBorder};
  border-radius: 8px;
  background: ${C.white};
  max-width: 1080px;
  margin: 0 auto 24px;
`;

const CardHeader = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${C.border};
`;

const CardHeaderTitle = styled.span`
  font-size: 14px;
  font-family: ${font};
  font-weight: 600;
  color: ${C.text};
`;

const CardBody = styled.div`
  padding: 20px;
`;

const ProductImage = styled(Image)`
  height: 80px;
  width: 80px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  background: ${(p) => p.$status === 'cancelled' ? C.redLight : p.$status === 'paused' ? C.amberLight : p.$status === 'completed' ? '#EDE9FE' : '#DCFCE7'};
  color: ${(p) => p.$status === 'cancelled' ? C.red : p.$status === 'paused' ? C.amber : p.$status === 'completed' ? '#5B21B6' : '#166534'};
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  font-family: ${font};
  font-weight: 600;
  flex-shrink: 0;
`;

const CardActions = styled.div`
  border-top: 1px solid ${C.border};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const TermsText = styled.div`
  margin-top: 16px;
  font-size: 12px;
  font-family: ${font};
  color: ${C.muted};
  line-height: 1.6;
`;

const FlowContainer = styled.div`
  max-width: 540px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const DoneContainer = styled.div`
  max-width: 540px;
  margin: 0 auto;
  padding: 48px 24px;
  text-align: center;
`;

const BackButton = styled.button`
  font-size: 13px;
  color: ${C.muted};
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${font};
  margin-bottom: 20px;
  padding: 0;
`;

const FlowTitle = styled.h2`
  font-family: Georgia, serif;
  font-size: 24px;
  color: ${C.text};
  margin-bottom: 6px;
  font-weight: 600;
`;

const FlowDescription = styled.p`
  color: ${C.muted};
  font-size: 14px;
  font-family: ${font};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const DoneDescription = styled.p`
  color: ${C.muted};
  font-size: 14px;
  font-family: ${font};
  line-height: 1.6;
  max-width: 420px;
  margin: 0 auto 24px;
`;

const IconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: ${C.greenLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
  color: ${C.greenMed};
`;

const DetailsCard = styled.div`
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 8px;
  padding: 16px;
  margin: 0 auto 24px;
  text-align: left;
  max-width: 360px;
`;

const DetailsLabel = styled.div`
  font-size: 12px;
  font-family: ${font};
  color: ${C.muted};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
`;

const DetailsContent = styled.div`
  font-size: 13px;
  font-family: ${font};
  color: ${C.text};
  line-height: 1.7;
`;

const AmberAlert = styled.div`
  background: ${C.amberLight};
  border: 1px solid ${C.amberBorder};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  font-size: 13px;
  font-family: ${font};
  color: ${C.amber};
  line-height: 1.6;
`;

const RedAlert = styled.div`
  background: ${C.redLight};
  border: 1px solid ${C.redBorder};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
`;

const PauseOfferCard = styled.div`
  background: ${C.greenLight};
  border: 2px solid ${C.greenAccent};
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 20px;
`;

const ContinueCancelCard = styled.div`
  border: 1px solid ${C.border};
  border-radius: 8px;
  padding: 20px;
`;

const LoseAccessCard = styled.div`
  border: 1px solid ${C.border};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  font-size: 13px;
  font-family: ${font};
  color: ${C.muted};
  line-height: 1.6;
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 20px;
`;

const ReasonLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: ${font};
  font-size: 13px;
  border: 1px solid ${(p) => p.$selected ? C.greenMed : C.border};
  background: ${(p) => p.$selected ? C.greenLight : C.white};
  color: ${(p) => p.$muted ? C.muted : C.text};
  font-style: ${(p) => p.$muted ? 'italic' : 'normal'};
  transition: all 0.15s;
  ${(p) => p.$fullWidth ? 'grid-column: 1 / -1;' : ''}
`;

const RadioCircle = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  border: 2px solid ${(p) => p.$selected ? C.greenMed : C.cardBorder};
  background: ${(p) => p.$selected ? C.greenMed : 'transparent'};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 3px;
  background: ${C.white};
`;

const SurveyTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 12px;
  font-size: 13px;
  font-family: ${font};
  border: 1px solid ${C.border};
  border-radius: 6px;
  resize: vertical;
  box-sizing: border-box;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  font-size: 14px;
  font-family: ${font};
  color: ${C.white};
  background: ${(p) => p.disabled ? C.greenAccent : C.greenMed};
  border: none;
  padding: 11px 28px;
  border-radius: 5px;
  cursor: ${(p) => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  opacity: ${(p) => p.disabled ? 0.8 : 1};
`;

const GhostButton = styled.button`
  font-size: 14px;
  font-family: ${font};
  color: ${C.muted};
  background: none;
  border: none;
  padding: 11px 16px;
  cursor: pointer;
`;

const OutlineButton = styled.button`
  font-size: 13px;
  font-family: ${font};
  color: ${C.greenMed};
  background: none;
  border: 1px solid ${C.greenMed};
  padding: 8px 20px;
  border-radius: 5px;
  cursor: pointer;
`;

const ActionOutlineButton = styled.button`
  font-size: 13px;
  font-family: ${font};
  color: ${C.greenMed};
  background: none;
  border: 1px solid ${C.greenMed};
  padding: 7px 18px;
  border-radius: 5px;
  cursor: pointer;
`;

const DangerOutlineButton = styled.button`
  font-size: 13px;
  font-family: ${font};
  color: ${C.red};
  background: ${C.white};
  border: 1px solid ${C.red};
  padding: 7px 18px;
  border-radius: 5px;
  cursor: pointer;
`;

const DangerButton = styled.button`
  font-size: 14px;
  font-family: ${font};
  color: ${C.white};
  background: ${(p) => p.disabled ? '#EF4444' : C.red};
  border: none;
  padding: 11px 24px;
  border-radius: 5px;
  cursor: ${(p) => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  opacity: ${(p) => p.disabled ? 0.8 : 1};
`;

const SecondaryButton = styled.button`
  font-size: 14px;
  font-family: ${font};
  color: ${C.muted};
  background: ${C.white};
  border: 1px solid ${C.border};
  padding: 11px 24px;
  border-radius: 5px;
  cursor: pointer;
`;

export default SubscriptionItem;
